import { Box, Container, FormControl, FormLabel, Input, useToast } from '@chakra-ui/react';
import OptionList from '../product/components/optionModal/components/optionList/OptionList';
import { useAppSelector } from '../../../hooks/redux';
import { useTranslation } from 'react-i18next';
import BuyButton from '../../buyButton/buyButton';
import { Link, useLocation } from 'react-router-dom';
import { IProduct } from '../../../interfaces';
import {
    calcProductOptionsPrice,
    getSelectedVariantIDs,
    sortOptionToTop,
} from '../../../hooks/option';
import Colors from '../../../config/colors';
import { FaChevronRight } from 'react-icons/fa';
import {
    useCreateCryptomusCheckoutInvoiceMutation,
    useCreateTonkeeperCheckoutInvoiceMutation,
    useGetUserQuery,
    useLazyGetUserQuery,
} from '../../../redux/platimarket/platimarket.api';
import { useTelegram } from '../../../hooks/useTelegram';
import { useEffect, useState } from 'react';
import PaymentList from '../../paymentList/PaymentList';
import { PAYMENT_LIST } from '../../../config/payments';
import { getFormatedPrice } from '../../../utils/price';
import { SendTransactionRequest, useTonConnectUI, useTonWallet } from '@tonconnect/ui-react';
import { createTransaction, ICreateTransaction } from '../../../utils/tonkeeper';
import { useLocaleType } from '../../../hooks/useLocaleType';

export default function CheckoutPage() {
    const [t, i18n] = useTranslation();
    const { localeType } = useLocaleType(i18n);

    const location = useLocation();
    const { user, onClose } = useTelegram();

    const product: IProduct = location.state?.product;
    const currency = product?.localeType.currency;

    let productOptions = useAppSelector((state) => state.productOptions.options);
    let options = productOptions.find((item) => item.localeType.name === localeType?.name)
        ?.options!;

    options = sortOptionToTop(options, 'select');

    const optionsPrice = calcProductOptionsPrice(options);
    const totalPrice = product.price + optionsPrice;

    const [steamProfileURL, setSteamProfileURL] = useState<string | null>(null);

    const selectedVariants = getSelectedVariantIDs(productOptions);

    const [currentPayment, setCurrentPayment] = useState<string>('tonkeeper');

    const isCanToBuy = product && currency && user && steamProfileURL;

    const toast = useToast();

    const wallet = useTonWallet();
    const [tonConnectUI, setOptions] = useTonConnectUI();

    const [
        fetchUser,
        { data: currentUser, isFetching: isLoadingGetUser, isSuccess: isSuccessGetUser },
    ] = useLazyGetUserQuery();

    const isInvalidSteamProfileURL = !isLoadingGetUser && !currentUser?.steamProfileURL;

    useEffect(() => {
        fetchUser();
    }, [])

    useEffect(() => {
        if (isSuccessGetUser && currentUser) {
            setSteamProfileURL(currentUser.steamProfileURL ?? null);
        }
    }, [isLoadingGetUser]);

    const [
        fetchCryptomusInvoice,
        {
            data: cryptomusInvoice,
            isSuccess: isSuccessCryptomusInvoice,
            isError: isErrorCryptomusInvoice,
            error: errorCryptomusInvoice,
        },
    ] = useCreateCryptomusCheckoutInvoiceMutation();

    const [
        fetchTonkeeperInvoice,
        {
            data: tonkeeperInvoice,
            isSuccess: isSuccessTonkeeperInvoice,
            isError: isErrorTonkeeperInvoice,
            error: errorTonkeeperInvoice,
        },
    ] = useCreateTonkeeperCheckoutInvoiceMutation();

    const createCryptomusInvoice = () => {
        if (isCanToBuy) {
            fetchCryptomusInvoice({
                productId: product.id,
                currency,
                gameActivationType: product.type,
                username: user.username!,
                selectedVariants,
            });
        }
    };

    const createTonkeeperInvoice = () => {
        if (isCanToBuy) {
            fetchTonkeeperInvoice({
                productId: product.id,
                currency,
                gameActivationType: product.type,
                username: user.username!,
                selectedVariants,
            });
        }
    };

    const onBuy = () => {
        if (currentPayment === 'tonkeeper') {
            if (wallet) {
                createTonkeeperInvoice();
            } else {
                tonConnectUI.openSingleWalletModal('tonkeeper');
            }
        } else if (currentPayment === 'cryptomus') {
            createCryptomusInvoice();
        }
    };

    const sendTransaction = async (data: ICreateTransaction) => {
        const transaction: SendTransactionRequest = createTransaction(data);

        tonConnectUI.sendTransaction(transaction);
    };

    useEffect(() => {
        if (isSuccessTonkeeperInvoice && tonkeeperInvoice) {
            sendTransaction({
                address: tonkeeperInvoice.payToAddress,
                amount: tonkeeperInvoice.amount,
                payload: tonkeeperInvoice.id,
            });
        }
    }, [isSuccessTonkeeperInvoice]);

    useEffect(() => {
        if (isSuccessCryptomusInvoice && cryptomusInvoice) {
            window.open(cryptomusInvoice.paymentURL, '_self');
        }
    }, [isSuccessCryptomusInvoice]);

    useEffect(() => {
        if (isErrorCryptomusInvoice || isErrorTonkeeperInvoice) {
            toast({
                description: t('product-is-out-of-stock'),
                status: 'error',
                duration: 10000,
            });
        }
    }, [isErrorCryptomusInvoice, isErrorTonkeeperInvoice]);

    useEffect(() => {
        const onSignTransacation = (e: any) => {
            console.log('Transaction signed: ', e.detail);
        };

        window.addEventListener('ton-connect-ui-transaction-signed', onSignTransacation);
    }, []);

    return (
        <Container height="calc(100% + 100px)" display="flex" flexDirection="column" gap="5">
            <FormControl fontWeight="300" isRequired={isInvalidSteamProfileURL}>
                <FormLabel fontSize="16px" color="#fff">
                    {t('link-to-you-steam-account')}
                </FormLabel>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    opacity="0.8"
                    as={Link}
                    to={'/settings'}
                    color="#fff"
                    background={Colors.darkBlue2}
                    border={isInvalidSteamProfileURL ? `1px solid ${Colors.red}` : 'none'}
                    borderRadius="7px"
                    p="0px 10px 0px 0px">
                    <Input
                        type="text"
                        background={Colors.darkBlue2}
                        color={Colors.lightGrey2}
                        value={steamProfileURL ?? ''}
                        border="none"
                        readOnly
                        fontSize="15px"
                        placeholder={t('please-enter-the-correct-link')}
                    />
                    <FaChevronRight fontSize="16px" />
                </Box>
            </FormControl>
            <FormControl fontWeight="300">
                <FormLabel fontSize="16px" color="#fff">
                    {t('select-options')}
                </FormLabel>
                <OptionList options={options} />
            </FormControl>

            <PaymentList
                payments={PAYMENT_LIST}
                currentPayment={currentPayment}
                setCurrentPayment={setCurrentPayment}
            />
            <Box
                position="fixed"
                display="flex"
                alignItems="center"
                bottom="6"
                zIndex="6"
                left="10px"
                right="10px">
                <BuyButton
                    name={`${t('buy')} ${getFormatedPrice(
                        totalPrice,
                        product?.localeType.currency,
                        product?.localeType.name,
                    )}`}
                    className="shine"
                    isDisabled={!isCanToBuy}
                    onClick={onBuy}
                />
            </Box>
        </Container>
    );
}
